import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { ConfirmationActionComponent } from 'app/components/ui/dialogs/confirmation-action/confirmation-action.component';
import { ConfirmationActionTypes } from 'app/components/ui/dialogs/confirmation-action/confirmation-action.types';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Inject } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { DocumentsService } from 'app/services/secretary/documents/documents.service';
import { MinuteInterface, MinuteTypeEnum, MinuteStatusEnum, MinuteTypeOptions, MinuteStatusOptions } from '../../minutes.interface';
import { generalConsts } from 'app/services/consts/general.consts';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { marked } from 'marked';

function normalizeNewlines(text: string): string
{
    return text
        .replace(/\\\n/g, '\n')
        .replace(/\\ /g, ' ')
        .replace(/\\n/g, '\n');
}

function parseMarkdownToHtml(md: string): string
{
    if (!md) return '';
    return marked.parse(normalizeNewlines(md), { async: false }) as string;
}

function convertTopicDetailsToHtml(items: any, showMeta: boolean = false): string
{
    if (!items) return '';

    if (typeof items === 'string') return parseMarkdownToHtml(items);

    if (!Array.isArray(items)) return String(items);

    return items
        .filter(item => item)
        .map(item => {
            const topic = item.topic || '';
            const detailsHtml = parseMarkdownToHtml(item.details || '');
            let metaHtml = '';
            if (showMeta && (item.responsible || item.deadline)) {
                const parts: string[] = [];
                if (item.responsible) parts.push(`<span class="font-[600]">Responsável:</span> ${item.responsible}`);
                if (item.deadline) {
                    const d = item.deadline.split('-');
                    const formatted = d.length === 3 ? `${d[2]}/${d[1]}/${d[0]}` : item.deadline;
                    parts.push(`<span class="font-[600]">Prazo:</span> ${formatted}`);
                }
                if (parts.length) {
                    metaHtml = `<div class="flex flex-wrap gap-3 mt-3 text-[13px] text-gray-500 border-t border-gray-200 pt-3">${parts.join('<span class="text-gray-300">|</span>')}</div>`;
                }
            }
            return `<div class="mb-4 last:mb-0"><h4 class="text-[15px] font-[600] text-gray-800 mb-2">${topic}</h4><div class="text-[14px] text-gray-600 leading-relaxed">${detailsHtml}</div>${metaHtml}</div>`;
        })
        .join('<hr class="my-4 border-gray-200">');
}
@Component({
    selector: 'app-minute-view-dialog',
    templateUrl: './view.component.html',
    styleUrls: ['./view.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        MatProgressBarModule,
        MatProgressSpinnerModule,
        MatDialogModule,
    ],
})
export class ViewComponent implements OnInit, OnDestroy
{
    loading: boolean = true;
    loadingDelete: boolean = false;
    loadingExport: boolean = false;
    minute: MinuteInterface;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _documentsService: DocumentsService,
        private _dialogRef: MatDialogRef<ViewComponent>,
        private _matDialog: MatDialog,
        private _sanitizer: DomSanitizer,
        @Inject(MAT_DIALOG_DATA) public data: { minuteData: MinuteInterface }
    )
    {
        marked.setOptions({ breaks: true, gfm: true });
    }

    ngOnInit(): void
    {
        if (this.data?.minuteData?.id) {
            setTimeout(() => {
                this.loadMinute(this.data.minuteData.id);
            });
        } else {
            this.minute = this.data.minuteData;
            this.loading = false;
        }
    }

    ngOnDestroy(): void
    {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    loadMinute(id: number): void
    {
        this.loading = true;

        this._documentsService.getMinuteById(id)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response: any) => {
                    this.minute = response.data;
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                }
            });
    }

    close(action: string = 'close'): void
    {
        this._dialogRef.close({ action });
    }

    edit(): void
    {
        this._dialogRef.close({ action: 'edit', data: this.minute });
    }

    exportReport(): void
    {
        if (!this.minute?.id) return;

        this.loadingExport = true;

        this._documentsService.exportMinuteReport(this.minute.id)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response: any) => {
                    this.loadingExport = false;

                    if (response?.fileUrl) {
                        const exportDialogData: ConfirmationActionTypes = {
                            title: 'Ata gerada',
                            type: 'success',
                            closeable: true,
                            yesButton: {
                                use: true,
                                label: 'Abrir',
                            },
                            noButton: { use: false },
                            cancelButton: { use: false },
                            closeButton: { use: true },
                            message: 'A ata em PDF da ' + (this.minute?.title || 'reunião') + ' foi gerada com sucesso, clique abaixo para abrir.',
                        };

                        const exportDialogRef = this._matDialog.open(ConfirmationActionComponent, {
                            autoFocus: false,
                            width: '95%',
                            maxWidth: '420px',
                            data: { data: exportDialogData },
                        });

                        exportDialogRef.afterClosed().subscribe((result) => {
                            if (result?.action === 'yes') {
                                window.open(response.fileUrl, '_blank');
                            }
                        });
                    }
                },
                error: () => {
                    this.loadingExport = false;
                }
            });
    }

    delete(): void
    {
        if (!this.minute?.id) return;

        const confirmationDeleteMinuteDataObject: ConfirmationActionTypes = {
            title: generalConsts.modules.secretary.documents.dialogs.confirmationDeleteMinuteDataObject.title,
            type: 'error',
            closeable: false,
            yesButton: { use: true, color: 'red' },
            noButton: { use: true },
            cancelButton: { use: false },
            message: generalConsts.modules.secretary.documents.dialogs.confirmationDeleteMinuteDataObject.message,
        };

        const dialogRef = this._matDialog.open(ConfirmationActionComponent, {
            autoFocus: false,
            width: '95%',
            maxWidth: '420px',
            data: {
                data: confirmationDeleteMinuteDataObject,
            },
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.action === 'yes') {
                this._performDelete();
            }
        });
    }

    private _performDelete(): void
    {
        if (!this.minute?.id) return;

        this.loadingDelete = true;

        this._documentsService.deleteMinute(this.minute.id)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: () => {
                    this._dialogRef.close({ action: 'reload' });
                },
                error: () => {
                    this.loadingDelete = false;
                }
            });
    }

    getTypeLabel(type: string): string
    {
        const labels: { [key: string]: string } = {
            [MinuteTypeEnum.GENERAL_ASSEMBLY]: 'Assembleia Geral',
            [MinuteTypeEnum.COUNCIL]: 'Conselho',
            [MinuteTypeEnum.BOARD]: 'Diretoria',
            [MinuteTypeEnum.MINISTERIAL_MEETING]: 'Reunião Ministerial',
            [MinuteTypeEnum.OTHER]: 'Outros'
        };
        return labels[type] || type;
    }

    getStatusLabel(status: string): string
    {
        const labels: { [key: string]: string } = {
            [MinuteStatusEnum.DRAFT]: 'Rascunho',
            [MinuteStatusEnum.APPROVED]: 'Aprovada',
            [MinuteStatusEnum.ARCHIVED]: 'Arquivada'
        };
        return labels[status] || status;
    }

    getParticipantsLabel(participants: number[] | any): string
    {
        if (!participants || (Array.isArray(participants) && participants.length === 0)) return '';

        if (Array.isArray(participants) && participants.length > 0) {
            return participants.join(', ');
        }

        if (typeof participants === 'string') return participants;

        return '';
    }

    getAgendaHtml(): SafeHtml
    {
        return this._sanitizer.bypassSecurityTrustHtml(convertTopicDetailsToHtml(this.minute?.agenda, false));
    }

    getDeliberationsHtml(): SafeHtml
    {
        return this._sanitizer.bypassSecurityTrustHtml(convertTopicDetailsToHtml(this.minute?.deliberations, true));
    }

    getSafeAudioUrl(): SafeResourceUrl
    {
        return this._sanitizer.bypassSecurityTrustResourceUrl(this.minute?.audioFileUrl || '');
    }
}
